'use client';

import { useEffect } from 'react';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { DangerButton, SecondaryButton } from '@/components/platform/buttons';
import { logger } from '@/lib/platform/utils/logger';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const { darkMode } = useDarkMode();

  useEffect(() => {
    // Log error to console and monitoring service
    logger.error('[Global Error Boundary]', error);
  }, [error]);

  return (
    <div
      className={`flex min-h-screen items-center justify-center ${
        darkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}
    >
      <div className="max-w-md px-4 text-center">
        <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Something went wrong!
        </h2>
        <p className={`mt-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {error.message || 'An unexpected error occurred'}
        </p>
        {error.digest && (
          <p className={`mt-2 font-mono text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Error ID: {error.digest}
          </p>
        )}
        <div className="mt-6 flex justify-center gap-4">
          <DangerButton onClick={reset}>Try again</DangerButton>
          <SecondaryButton
            // Intentional full-page reload: error boundary state must be fully reset
            // eslint-disable-next-line @next/next/no-location-assign-relative-destination
            onClick={() => (window.location.href = '/dashboard')}
          >
            Go to Dashboard
          </SecondaryButton>
        </div>
      </div>
    </div>
  );
}
